import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  sendEmailVerification,
  updateProfile,
  signOut,
  type User,
} from 'firebase/auth'
import { doc, setDoc, serverTimestamp } from 'firebase/firestore'
import { httpsCallable, type HttpsCallable } from 'firebase/functions'
import { ref as storageRef, uploadString } from 'firebase/storage'
import { auth, db, functions, storage, isFirebaseConfigured } from '@/lib/firebase'
import { useRecaptcha } from '@/composables/useRecaptcha'
import type { SkillLevel } from '@/stores/courts'
import {
  LIABILITY_WAIVER_VERSION,
  PRIVACY_POLICY_VERSION,
  TERMS_VERSION,
} from '@/lib/legalVersions'

function requireFirebase() {
  if (!isFirebaseConfigured) {
    throw new Error('Firebase is not configured — add VITE_FIREBASE_* to .env.local (see .env.example).')
  }
}

// functions is null until a real Firebase project is configured (see
// src/lib/firebase.ts) — httpsCallable() would throw at module load time
// otherwise, since this store is imported by the router guard on every page.
const checkLoginLock: HttpsCallable | null = functions ? httpsCallable(functions, 'checkLoginLock') : null
const recordFailedLogin: HttpsCallable | null = functions ? httpsCallable(functions, 'recordFailedLogin') : null
const resetFailedLogins: HttpsCallable | null = functions ? httpsCallable(functions, 'resetFailedLogins') : null
// Links any guest bookings made under this account's email (before the
// account existed, or while signed out) by setting their userId — see that
// function's own comment. Best-effort everywhere it's called: a booking a
// player already knows about not yet showing up on their dashboard is a
// much smaller problem than login/registration itself failing over it.
const claimGuestBookings: HttpsCallable<unknown, { claimed: number }> | null = functions
  ? httpsCallable(functions, 'claimGuestBookings')
  : null
const deactivateAccountFn: HttpsCallable | null = functions
  ? httpsCallable(functions, 'deactivateAccount')
  : null

/**
 * `admin-viewer` — read-only admin access (added 2026-08-05): can sign in
 * and browse every admin/staff page exactly like `admin`, but every write
 * is rejected. This is enforced server-side, not by the frontend: every
 * Cloud Function's role check and every firestore.rules write rule use
 * strict equality against `'admin'`/`'staff'`, so a distinct role string
 * is automatically excluded from all of them with no per-function change
 * needed — see setUserRole's `ASSIGNABLE_ROLES` and firestore.rules'
 * `isAdminViewer()`/`canReadStaffOrAdmin()` for the read-access half.
 */
export type UserRole = 'guest' | 'player' | 'coach' | 'staff' | 'admin' | 'admin-viewer'

export const roleLabels: Record<UserRole, string> = {
  guest: 'Guest',
  player: 'Player',
  coach: 'Coach',
  staff: 'Staff',
  admin: 'Admin',
  'admin-viewer': 'Admin (View Only)',
}

export const useAuthStore = defineStore('auth', () => {
  const firebaseUser = ref<User | null>(null)
  const role = ref<UserRole>('guest')
  const isReady = ref(false)

  const isAuthenticated = computed(() => firebaseUser.value !== null)
  const uid = computed(() => firebaseUser.value?.uid ?? null)
  const email = computed(() => firebaseUser.value?.email ?? null)
  const displayName = computed(() => firebaseUser.value?.displayName ?? null)

  if (auth) {
    onAuthStateChanged(auth, async (user) => {
      firebaseUser.value = user
      if (user) {
        // Role lives in a custom claim, set server-side by a Cloud Function on
        // signup/approval (see firestore.rules — hasRole() reads this same
        // claim, never a client-writable field).
        const token = await user.getIdTokenResult()
        role.value = (token.claims.role as UserRole | undefined) ?? 'guest'
      } else {
        role.value = 'guest'
      }
      isReady.value = true
    })
  } else {
    // No backend configured — resolve immediately so the router guard (which
    // awaits isReady) doesn't hang forever waiting for onAuthStateChanged.
    isReady.value = true
  }

  async function login(email: string, password: string) {
    requireFirebase()
    // Server-side lockout check (Admin SDK), not just client-side — see
    // functions/src/index.ts. Deliberately checked before every attempt,
    // not just after N client-observed failures, since the client can't be
    // trusted to enforce this on its own.
    await checkLoginLock!({ email })
    try {
      await signInWithEmailAndPassword(auth!, email, password)
    } catch (err) {
      // recordFailedLogin mutates another account's lockout state and can't
      // require an authenticated caller (login hasn't succeeded yet), so the
      // Cloud Function verifies this token server-side before doing anything
      // — without it, this call is an open "lock out any email" endpoint.
      const { getToken } = useRecaptcha()
      const recaptchaToken = await getToken('login')
      await recordFailedLogin!({ email, recaptchaToken })
      throw err
    }
    // No email/uid passed — the Cloud Function derives the target from this
    // (now-authenticated) request's own ID token, so nobody but the account
    // itself can ever clear its own lockout counter.
    //
    // Best-effort, and that matters: signInWithEmailAndPassword has ALREADY
    // succeeded by this line, so the person is authenticated whatever happens
    // next. Awaiting this uncaught meant a failure here threw out of login()
    // and the UI reported a failed sign-in for a session that was actually
    // live — which is precisely what locked out every account whose users/
    // document was missing, since resetFailedLogins threw NOT_FOUND for them
    // on every single attempt. Failing to clear a lockout counter is a far
    // smaller problem than refusing entry to someone who just proved who they
    // are; same reasoning as claimGuestBookings on the next line.
    await resetFailedLogins!().catch(() => null)
    // Best-effort, not awaited-and-blocking-login — see claimGuestBookings'
    // own comment on why this is safe to call on every login, not just once
    // at registration.
    void claimGuestBookings?.().catch(() => null)
  }

  async function register(input: {
    email: string
    password: string
    phone?: string
    firstName: string
    lastName: string
    skillLevel?: SkillLevel
    /** "YYYY-MM-DD" — RegisterPage.vue collects and requires this but previously never sent it here, so every registration silently discarded the date of birth the form made the user fill in. */
    dob?: string
  }) {
    requireFirebase()
    // Normalised ONCE, and everything below uses this rather than input.email.
    //
    // This is load-bearing, not tidiness. Firebase Auth lowercases the address
    // it stores, while the profile write below sends whatever the form
    // collected — and firestore.rules' users `create` rule requires
    // `data.email == request.auth.token.email` exactly. So a single capital
    // letter made the two disagree, the profile write was denied, and because
    // createUserWithEmailAndPassword had already succeeded the account was
    // left existing in Auth with no users/ document.
    //
    // That state is unrecoverable for the person: registering again fails with
    // email-already-in-use, and signing in used to throw too (see login()).
    // Measured in production 2026-09-05: 63 of 1,807 accounts had no profile
    // document. Reproduced against the real rules on the emulator.
    const email = input.email.trim().toLowerCase()
    const credential = await createUserWithEmailAndPassword(auth!, email, input.password)
    // Firebase Auth's own profile only has a single displayName field (no
    // first/last split) — set it for anywhere that reads the Auth user
    // directly (e.g. the `displayName` computed below) without needing a
    // Firestore read. firstName/lastName in Firestore remain the source of
    // truth for anything that needs them separately.
    await updateProfile(credential.user, {
      displayName: `${input.firstName} ${input.lastName}`.trim(),
    })
    // No custom claim exists yet at this point, so this write must match the
    // `create` rule in firestore.rules: role/isApproved forced to their
    // unprivileged defaults, only escalated later via Admin SDK.
    await setDoc(doc(db!, 'users', credential.user.uid), {
      // The normalised value, which is what Auth put in the token — see above.
      email,
      firstName: input.firstName,
      lastName: input.lastName,
      phone: input.phone ?? '',
      photoUrl: '',
      bio: '',
      ...(input.skillLevel ? { skillLevel: input.skillLevel } : {}),
      ...(input.dob ? { dob: input.dob } : {}),
      role: 'guest',
      isApproved: false,
      failedLoginAttempts: 0,
      lockedUntil: null,
      // RegisterPage.vue blocks submission until the waiver checkbox is agreed
      // to, so this is always true by the time register() is called.
      waiverAccepted: true,
      // Same signature/checkbox also covers the Privacy Policy and Terms of
      // Service (see WaiverModal.vue's paragraphs 7-8) — one consent record
      // versioning all three documents rather than a separate mechanism.
      waiver: {
        signedAt: serverTimestamp(),
        liabilityWaiverVersion: LIABILITY_WAIVER_VERSION,
        privacyPolicyVersion: PRIVACY_POLICY_VERSION,
        termsVersion: TERMS_VERSION,
      },
      createdAt: serverTimestamp(),
    })
    // Best-effort — nothing in the app currently gates any feature on
    // email verification (that's still an open decision, see
    // PROJECT_STATUS.md), so a failure here shouldn't block registration.
    await sendEmailVerification(credential.user).catch(() => null)
    // Links any guest bookings made under this email before the account
    // existed — see claimGuestBookings' own comment. Best-effort, not
    // awaited-and-blocking registration.
    void claimGuestBookings?.().catch(() => null)
    return credential.user
  }

  /**
   * Uploads the injury waiver e-signature (a PNG data URL from
   * WaiverModal.vue's signature pad) to Storage at waivers/{uid}/signature.png.
   * Best-effort — the account already exists by the time this runs, so a
   * failure here shouldn't undo registration or block the user from
   * proceeding; storage.rules only allows a single create per uid anyway
   * (no update/delete), matching a signed record rather than an editable field.
   */
  async function uploadWaiverSignature(uid: string, signatureDataUrl: string) {
    requireFirebase()
    const fileRef = storageRef(storage!, `waivers/${uid}/signature.png`)
    await uploadString(fileRef, signatureDataUrl, 'data_url')
  }

  async function logout() {
    requireFirebase()
    await signOut(auth!)
  }

  /** Data Privacy Act right-to-delete — see deactivateAccount (functions/src/index.ts) for what actually happens server-side. Caller must sign out afterward; this only marks the account inactive/disabled server-side, it doesn't end the current client session by itself. */
  async function deactivateAccount() {
    requireFirebase()
    await deactivateAccountFn!()
  }


  async function resetPassword(email: string) {
    requireFirebase()
    await sendPasswordResetEmail(auth!, email)
  }

  return {
    firebaseUser,
    role,
    isReady,
    isAuthenticated,
    uid,
    email,
    displayName,
    login,
    register,
    logout,
    resetPassword,
    uploadWaiverSignature,
    deactivateAccount,
  }
})
